import type {
  CampaignDetailDto,
  CatalogTreeNodeDto,
  CatalogTreeProfileDto,
  CatalogTreeSubFamilyDto,
  SeniorityLevelDto,
  TargetPriceConfigDto,
} from '@/api/campaign-client';
import { SENIORITY_LEVELS } from '@/lib/campaign-labels';

// =============================================================================
// profile-scope-model.ts — Logique PURE du périmètre profils (catalogue + prix cibles) du détail campagne.
// =============================================================================
// Extrait de campaign-detail-screen.tsx (refonte 2026-09-16) : ACL de séniorité catalog → campaign, aplatissement
// de l'arbre, filtre mots-clés (projecteur, jamais un état), compteurs du catalogue et tableau PIVOTÉ des prix
// cibles (une ligne par profil, une colonne par niveau). Aucune règle métier nouvelle : l'autorité reste l'API.

/** Un profil aplati de l'arbre (avec sa famille/sous-famille) — pour le filtre mots-clés et l'expansion. */
export interface FlatTreeProfile {
  readonly profileCode: string;
  readonly libelle: string;
  readonly famille: string;
  readonly sousFamille: string;
  readonly availableLevels: readonly SeniorityLevelDto[];
}

/**
 * ACL de niveau de séniorité (frontière catalog → campaign). Le référentiel CATALOG expose les
 * libellés ACCENTUÉS (`Débutant`/`Maîtrisant`/`Référent`) ; le BC campaign attend l'enum Prisma NON
 * ACCENTUÉ (`Debutant`/`Maitrisant`/`Referent`, cf. `SeniorityLevel` @jeko/db). Sans cette traduction,
 * l'ajout au périmètre POSTe un niveau accentué → 422 « seniorityLevel invalide ». Idempotent : accepte
 * déjà-non-accentué. Renvoie `null` pour un libellé inconnu (écarté, jamais envoyé).
 */
const CATALOG_TO_CAMPAIGN_SENIORITY: Readonly<Record<string, SeniorityLevelDto>> = {
  Débutant: 'Debutant',
  Debutant: 'Debutant',
  Maîtrisant: 'Maitrisant',
  Maitrisant: 'Maitrisant',
  Expert: 'Expert',
  Référent: 'Referent',
  Referent: 'Referent',
};
export function toCampaignSeniority(level: string): SeniorityLevelDto | null {
  return CATALOG_TO_CAMPAIGN_SENIORITY[level] ?? null;
}

/** Niveaux d'un profil catalogue traduits vers l'enum campaign (inconnus écartés). */
export function campaignLevelsOf(profile: CatalogTreeProfileDto): SeniorityLevelDto[] {
  return profile.availableLevels
    .map((l) => toCampaignSeniority(l))
    .filter((l): l is SeniorityLevelDto => l !== null);
}

/** Aplati l'arbre du périmètre en liste de profils (chacun portant famille/sous-famille). */
export function flattenTree(tree: readonly CatalogTreeNodeDto[]): FlatTreeProfile[] {
  const out: FlatTreeProfile[] = [];
  for (const fam of tree) {
    for (const sf of fam.sousFamilles) {
      for (const p of sf.profils) {
        out.push({
          profileCode: p.profileCode,
          libelle: p.libelle,
          famille: fam.famille,
          sousFamille: sf.sousFamille,
          availableLevels: campaignLevelsOf(p),
        });
      }
    }
  }
  return out;
}

// -----------------------------------------------------------------------------
// Catalogue : filtre et compteurs
// -----------------------------------------------------------------------------

/** Sous-famille projetée par le filtre : profils visibles (vide = « aucun match ») et total catalogue. */
export interface FilteredSubFamily {
  readonly sousFamille: string;
  readonly profils: readonly CatalogTreeProfileDto[];
  readonly total: number;
}

export interface FilteredFamily {
  readonly famille: string;
  readonly sousFamilles: readonly FilteredSubFamily[];
  /** Sous-familles ayant au moins un profil visible. */
  readonly matchedSubFamilies: number;
}

export interface FilteredCatalog {
  readonly families: readonly FilteredFamily[];
  readonly matchedProfiles: number;
  readonly matchedSubFamilies: number;
}

/**
 * Filtre mots-clés (code, libellé, famille, sous-famille ; insensible à la casse). Le filtre est un
 * PROJECTEUR : il ne touche jamais la sélection. Une famille sans aucun match est masquée ; dans une famille
 * retenue, TOUTES les sous-familles restent listées, celles sans match avec `profils` vide (rendues grisées,
 * repliées, « aucun match »). Mot-clé vide : tout est visible.
 */
export function filterCatalog(
  tree: readonly CatalogTreeNodeDto[],
  keyword: string,
): FilteredCatalog {
  const kw = keyword.trim().toLowerCase();
  const families: FilteredFamily[] = [];
  let matchedProfiles = 0;
  let matchedSubFamilies = 0;
  for (const fam of tree) {
    const sousFamilles: FilteredSubFamily[] = fam.sousFamilles.map(
      (sf: CatalogTreeSubFamilyDto) => ({
        sousFamille: sf.sousFamille,
        total: sf.profils.length,
        profils:
          kw.length === 0
            ? sf.profils
            : sf.profils.filter((p) =>
                [p.profileCode, p.libelle, fam.famille, sf.sousFamille].some((f) =>
                  f.toLowerCase().includes(kw),
                ),
              ),
      }),
    );
    const matched = sousFamilles.filter((sf) => sf.profils.length > 0);
    if (matched.length === 0) {
      continue;
    }
    families.push({
      famille: fam.famille,
      sousFamilles,
      matchedSubFamilies: matched.length,
    });
    matchedSubFamilies += matched.length;
    matchedProfiles += matched.reduce((n, sf) => n + sf.profils.length, 0);
  }
  return { families, matchedProfiles, matchedSubFamilies };
}

/** État tri-état d'un nœud, dérivé de ses descendants SÉLECTIONNABLES visibles. */
export function nodeState(
  codes: readonly string[],
  selected: ReadonlySet<string>,
): 'all' | 'partial' | 'none' {
  if (codes.length === 0) {
    return 'none';
  }
  const selectedCount = codes.filter((c) => selected.has(c)).length;
  if (selectedCount === 0) {
    return 'none';
  }
  return selectedCount === codes.length ? 'all' : 'partial';
}

/** Niveaux déjà au périmètre de la campagne, par code profil. */
export function scopedLevelsByProfile(
  profileScopes: CampaignDetailDto['profileScopes'],
): ReadonlyMap<string, ReadonlySet<SeniorityLevelDto>> {
  const m = new Map<string, Set<SeniorityLevelDto>>();
  for (const s of profileScopes) {
    const set = m.get(s.profileCode) ?? new Set<SeniorityLevelDto>();
    set.add(s.seniorityLevel);
    m.set(s.profileCode, set);
  }
  return m;
}

/**
 * Un profil catalogue est « au périmètre » s'il y a au moins un de ses niveaux ET qu'il n'en manque aucun :
 * sa case est alors désactivée (GATE_HUMAN 2026-09-16). Un profil partiellement au périmètre reste
 * sélectionnable : l'ajout (idempotent côté API) complète les niveaux manquants.
 */
export function isFullyScoped(
  levels: readonly SeniorityLevelDto[],
  scoped: ReadonlySet<SeniorityLevelDto> | undefined,
): boolean {
  return scoped !== undefined && scoped.size > 0 && levels.every((l) => scoped.has(l));
}

/** Nombre de cellules (couples profil × niveau) réellement ajoutées par la sélection. */
export function cellsToAdd(
  profiles: readonly FlatTreeProfile[],
  selected: ReadonlySet<string>,
  scoped: ReadonlyMap<string, ReadonlySet<SeniorityLevelDto>>,
): number {
  return profiles
    .filter((p) => selected.has(p.profileCode))
    .reduce((n, p) => {
      const already = scoped.get(p.profileCode);
      return n + p.availableLevels.filter((l) => already?.has(l) !== true).length;
    }, 0);
}

// -----------------------------------------------------------------------------
// Prix cibles : tableau pivoté (une ligne par profil, une colonne par niveau)
// -----------------------------------------------------------------------------

/** Cellule d'un niveau AU PÉRIMÈTRE : le scope et son Prix Cible courant. */
export interface PivotCell {
  readonly scopeId: string;
  readonly target: number | null;
}

export interface PivotRow {
  readonly profileCode: string;
  readonly libelle: string;
  /** Niveau → cellule ; un niveau absent n'est PAS au périmètre (rendu « — », non éditable). */
  readonly cells: Readonly<Partial<Record<SeniorityLevelDto, PivotCell>>>;
  /** Tous les scopeId du profil (retrait de la ligne). */
  readonly scopeIds: readonly string[];
}

export interface PivotGroup {
  readonly key: string;
  readonly famille: string;
  readonly sousFamille: string;
  readonly rows: readonly PivotRow[];
}

/**
 * Pivot des scopes profils : groupe « Famille › Sous-famille » → une ligne par profil, une cellule par
 * niveau au périmètre. Ordre d'apparition préservé (groupes, profils). Les données restent strictement
 * celles des scopes : seule la présentation change (GATE_HUMAN 2026-09-16).
 */
export function buildPivot(
  profileScopes: CampaignDetailDto['profileScopes'],
  tree: readonly CatalogTreeNodeDto[] | undefined,
  targetPrices: readonly TargetPriceConfigDto[] | undefined,
): readonly PivotGroup[] {
  const meta = new Map<string, FlatTreeProfile>();
  for (const p of tree === undefined ? [] : flattenTree(tree)) {
    meta.set(p.profileCode, p);
  }
  const targets = new Map<string, number>();
  for (const t of targetPrices ?? []) {
    targets.set(`${t.profileCode}::${t.seniorityLevel}`, t.targetPriceEur);
  }

  const groups = new Map<
    string,
    { famille: string; sousFamille: string; rows: Map<string, MutablePivotRow> }
  >();
  for (const scope of profileScopes) {
    const m = meta.get(scope.profileCode);
    const famille = m?.famille ?? 'Non classé';
    const sousFamille = m?.sousFamille ?? '—';
    const key = `${famille}::${sousFamille}`;
    const group = groups.get(key) ?? { famille, sousFamille, rows: new Map() };
    const row = group.rows.get(scope.profileCode) ?? {
      profileCode: scope.profileCode,
      libelle: m?.libelle ?? scope.profileCode,
      cells: {},
      scopeIds: [],
    };
    row.cells[scope.seniorityLevel] = {
      scopeId: scope.scopeId,
      target: targets.get(`${scope.profileCode}::${scope.seniorityLevel}`) ?? null,
    };
    row.scopeIds.push(scope.scopeId);
    group.rows.set(scope.profileCode, row);
    groups.set(key, group);
  }
  return [...groups.entries()].map(([key, g]) => ({
    key,
    famille: g.famille,
    sousFamille: g.sousFamille,
    rows: [...g.rows.values()],
  }));
}

interface MutablePivotRow {
  profileCode: string;
  libelle: string;
  cells: Partial<Record<SeniorityLevelDto, PivotCell>>;
  scopeIds: string[];
}

/** Nombre de cellules (niveaux au périmètre) d'un pivot. */
export function pivotCellCount(groups: readonly PivotGroup[]): number {
  return groups.reduce(
    (n, g) =>
      n +
      g.rows.reduce(
        (m, r) => m + SENIORITY_LEVELS.filter((l) => r.cells[l] !== undefined).length,
        0,
      ),
    0,
  );
}

/** Nombre de profils (lignes) d'un pivot. */
export function pivotProfileCount(groups: readonly PivotGroup[]): number {
  return groups.reduce((n, g) => n + g.rows.length, 0);
}
